import { Link } from 'react-router';
import { ArrowRight, ChartColumn, ExternalLink, Images, Newspaper, Plus, Settings, UserX, Users } from 'lucide-react';
import { Card, PageHeader, Spinner, Stat } from '@lp/portal-ui';
import type { BrandReport, DeletionRequestAdmin } from '@lp/shared';
import { api } from '../api.ts';
import { fmtNumber, last30Days } from '../lib/format.ts';
import { useLoad } from '../lib/useLoad.ts';
import { LoadError } from '../components/ui.tsx';
import { useSession } from '../session.tsx';

const quickLinks = [
  { to: '/news/new', label: '發佈最新消息', desc: '新增優惠或活動消息', icon: Plus },
  { to: '/banners', label: '管理主頁橫額', desc: '更換會員網站主頁大圖', icon: Images },
  { to: '/members', label: '會員名單', desc: '搜尋及查看會員資料', icon: Users },
  { to: '/settings', label: '網站設定', desc: '外觀、語言、註冊欄位', icon: Settings },
  { to: '/reports', label: '報表', desc: '新會員、登入及換領數字', icon: ChartColumn },
  { to: '/news', label: '全部消息', desc: '編輯或下架現有消息', icon: Newspaper },
];

export default function DashboardPage() {
  const { me } = useSession();
  const range = last30Days();
  const summary = useLoad(() => api.get<BrandReport>(`/reports/summary?from=${range.from}&to=${range.to}`), []);
  const pending = useLoad(() => api.get<DeletionRequestAdmin[]>('/deletion-requests?status=pending'), []);
  const pendingCount = pending.data?.length ?? 0;

  return (
    <>
      <PageHeader
        title="主控台"
        description={`你好，${me.user.name}。以下是 ${me.brand.name} 最近 30 日的概況。`}
        actions={
          <a
            href={me.brand.memberSiteUrl}
            target="_blank"
            rel="noopener"
            className="inline-flex h-10 items-center gap-2 rounded-full bg-white px-5 text-[15px] font-medium text-ink ring-1 ring-inset ring-ink/25 transition-colors hover:bg-mint"
          >
            查看會員網站
            <ExternalLink className="size-4" />
          </a>
        }
      />

      {summary.error ? (
        <Card className="mb-6">
          <LoadError error={summary.error} onRetry={summary.reload} />
        </Card>
      ) : !summary.data ? (
        <Spinner />
      ) : (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
          <Stat label="新會員" value={fmtNumber(summary.data.totals.newMembers)} hint="最近 30 日" tone="brand" />
          <Stat label="會員登入" value={fmtNumber(summary.data.totals.logins)} hint="最近 30 日" tone="teal" />
          <Stat label="積分換領" value={fmtNumber(summary.data.totals.redemptions)} hint="最近 30 日" tone="amber" />
          <Stat label="會員總數" value={fmtNumber(summary.data.totalMembers)} hint="截至今日" tone="sky" />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="待處理事項" className="lg:col-span-1">
          {pending.error ? (
            <LoadError error={pending.error} onRetry={pending.reload} />
          ) : pending.loading && !pending.data ? (
            <Spinner />
          ) : (
            <Link
              to="/deletion-requests"
              className="group flex items-center gap-4 rounded-xl bg-canvas p-4 ring-1 ring-line transition-colors hover:bg-mint"
            >
              <div className={pendingCount > 0 ? 'rounded-full bg-coral/15 p-3 text-coral' : 'rounded-full bg-brand/15 p-3 text-brand-700'}>
                <UserX className="size-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[28px] font-medium leading-none text-ink">{pendingCount}</div>
                <div className="mt-1 text-sm text-muted">{pendingCount > 0 ? '個刪除帳戶申請等待處理' : '沒有待處理的刪除帳戶申請'}</div>
              </div>
              <ArrowRight className="size-5 text-muted transition-transform group-hover:translate-x-0.5" />
            </Link>
          )}
        </Card>

        <Card title="快速連結" className="lg:col-span-2">
          <div className="grid gap-3 sm:grid-cols-2">
            {quickLinks.map(({ to, label, desc, icon: Icon }) => (
              <Link key={to} to={to} className="group flex items-center gap-3 rounded-xl p-3 ring-1 ring-line transition-colors hover:bg-mint">
                <div className="rounded-full bg-mint p-2.5 text-ink group-hover:bg-white">
                  <Icon className="size-[18px]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[15px] font-medium text-ink">{label}</div>
                  <div className="truncate text-xs text-muted">{desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
